import axios from 'axios';
import { environment } from '@environments/environment';
import { ProductsPage, ProductsParam, SaleType, TopSellingExportParam } from '@models/Products';
import { constructRequest } from '@utils/serviceUtils/RequestFormatter';
import { RequestPayloadArgumentObject } from '@pages/product-analyzer/Product';
import { getQueryClient } from '../queries/QueryClientWrapper';
import { throwApiError } from '@services/utils/ErrorHandlers';

export const getProductsLeaderboard = async (param: ProductsParam): Promise<ProductsPage> => {
  let body: any;
  try {
    const qc = getQueryClient('products');
    const sortKey = param.sortOption.label?.toUpperCase();
    const requestPayloadArgumentObject: RequestPayloadArgumentObject = {
      clientId: param.clientId,
      productType: param.productType,
      countryId: [param.countryId],
      productGroup: param.productGroup,
      periodType: param.periodType,
      period: param.period,
      comparisonPeriod: param.comparisonPeriod,
      appliedFilters: param.appliedFilters,
      kpi: param.kpi,
      calculateRollingPeriod: false,
      page: param.pageRequest.page,
      size: param.pageRequest.size,
      sortBy: param.sortOption.headerKey,
      sortDirection: param.sortOption.sortDir,
      sortKey,
      isMarket: param.isMarket,
      comparisonGrowth: param.growthType,
      productGrouping: param.groupDetails,
      isUsingCollectionLiveData: param.isUsingCollectionLiveData,
    };

    const queryKey = ['products-leaderboard', requestPayloadArgumentObject] as const;

    const url = `${environment.baseUrl}/v2/products/data`;
    body = constructRequest(requestPayloadArgumentObject);

    return await qc.fetchQuery<ProductsPage>({
      queryKey,
      queryFn: async ({ signal }) => {
        const { data } = await axios.post<ProductsPage>(url, body, { signal });
        data.content = data.content?.map((kpi) =>
          kpi.item?.itemName === 'TOTAL' ? { ...kpi, item: { ...kpi.item, itemBrand: '' } } : kpi,
        );
        return data;
      },
      retry: false,
    });
  } catch (err: any) {
    // Delegated error handling; helper always throws. Added explicit throw for Sonar compliance.
    throwApiError(err, { excludedBrand: body?.excludedBrand }); // NOSONAR
  }
};

export const getExportTopSellingProducts = async (param: TopSellingExportParam): Promise<ProductsPage> => {
  try {
    const payloadArgumentObject: RequestPayloadArgumentObject = {
      productType: param.productType,
      comparisonGrowth: undefined,
      appliedFilters: param.appliedFilters,
      isMarket: false,
      kpi: SaleType.revenue,
      page: undefined,
      size: undefined,
      clientId: param.clientId,
      countryId: [param.countryId],
      productGroup: param.productGroup,
      periodType: param.periodType,
      period: param.period,
      comparisonPeriod: param.comparisonPeriod,
      calculateRollingPeriod: false,
      productGrouping: param.groupDetails,
      sortBy: param.sortBy,
      sortDirection: param.sortDirection,
      sortKey: param.sortKey,
    };

    const { data } = await axios.post<ProductsPage>(
      `${environment.baseUrl}/v2/products/data?isReport=true`,
      constructRequest(payloadArgumentObject),
    );

    return data;
  } catch (e) {
    throw new Error(`An error has occurred: ${e}`);
  }
};
